export const API = process.env.REACT_APP_BACKEND_URL;

export const CATEGORY_META = {
  textbook: { label: 'Textbook', color: 'bg-sky-50 text-sky-800 border-sky-200', accent: 'border-l-sky-500' },
  activities_booklet: { label: 'Activities Booklet', color: 'bg-violet-50 text-violet-800 border-violet-200', accent: 'border-l-violet-500' },
  answer_key: { label: 'Answer Key', color: 'bg-rose-50 text-rose-800 border-rose-200', accent: 'border-l-rose-500' },
  study_guide: { label: 'Student Study Guide', color: 'bg-emerald-50 text-emerald-800 border-emerald-200', accent: 'border-l-emerald-500' },
  year_planner: { label: 'Year Planner', color: 'bg-amber-50 text-amber-800 border-amber-200', accent: 'border-l-amber-500' },
  scheme_of_work: { label: 'Scheme of Work', color: 'bg-orange-50 text-orange-800 border-orange-200', accent: 'border-l-orange-500' },
  daily_lesson_plan: { label: 'Daily Lesson Plans', color: 'bg-teal-50 text-teal-800 border-teal-200', accent: 'border-l-teal-500' },
  pupil_book: { label: 'Pupil Book', color: 'bg-sky-50 text-sky-800 border-sky-200', accent: 'border-l-sky-500' },
  activity_book: { label: 'Activity Book', color: 'bg-violet-50 text-violet-800 border-violet-200', accent: 'border-l-violet-500' },
  teacher_guide: { label: 'Teacher Guide', color: 'bg-rose-50 text-rose-800 border-rose-200', accent: 'border-l-rose-500' },
  assessment_checklist: { label: 'Assessment Checklist', color: 'bg-emerald-50 text-emerald-800 border-emerald-200', accent: 'border-l-emerald-500' },
};

export const PRIMARY_CATEGORIES = [
  { key: 'pupil_book', hint: 'The book pupils read and work from in class.' },
  { key: 'activity_book', hint: 'Exercises and activities for pupils to complete.' },
  { key: 'teacher_guide', hint: 'Lesson notes and methodology for the teacher.', staff: true },
  { key: 'year_planner', hint: 'Upload the Excel year planner below to get the interactive week-by-week planner; PDF copies can also be attached here.', staff: true },
  { key: 'assessment_checklist', hint: 'Outcome checklists to record each pupil\'s progress.', staff: true },
];

export const TEACHING_CATEGORIES = ['textbook', 'activities_booklet', 'answer_key', 'study_guide'];
export const PLANNING_CATEGORIES = ['year_planner', 'scheme_of_work', 'daily_lesson_plan'];

export const PLANNING_STEPS = [
  { key: 'year_planner', step: 1, tab: 'year', hint: 'Start here — see which topics fall in which term and week.' },
  { key: 'scheme_of_work', step: 2, tab: 'scheme', hint: 'Then open the week you are teaching: outcomes, content, activities, assessment.' },
  { key: 'daily_lesson_plan', step: 3, tab: 'lessons', hint: 'Finally pick the matching daily lesson plan and teach it as written.' },
];

export const WORKBOOK_ONLY_STEPS = [
  { key: 'strategies', step: 4, tab: 'strategies', flag: 'has_strategies', label: 'Teaching Strategies', color: 'bg-cyan-50 text-cyan-800 border-cyan-200', hint: 'HITS, group work, formative assessment, CLIL and scaffolding — and where each is used in the lessons.' },
  { key: 'assessment_plan', step: 5, tab: 'assessment', flag: 'has_assessment_plan', label: 'Assessment Plan', color: 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200', hint: 'Test blueprints per chapter (outcomes × SOLO levels), rubrics and how to write a test.' },
];

export function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function resourceUrl(res, token, download = false) {
  if (res.is_external) return `${API}${res.url}`;
  const base = `${API}${res.url.split('?')[0]}`;
  const params = new URLSearchParams({ token });
  if (download) params.set('download', '1');
  return `${base}?${params.toString()}`;
}

const CHUNK_SIZE = 5 * 1024 * 1024;

export async function uploadResourceChunked({ file, subject, yearLevel, category, title, description, onProgress }) {
  const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
  const init = new FormData();
  init.append('subject', subject);
  init.append('year_level', String(yearLevel));
  init.append('category', category);
  init.append('title', title);
  init.append('description', description || '');
  init.append('file_name', file.name);
  init.append('file_size', String(file.size));
  init.append('mime_type', file.type || 'application/pdf');
  const initRes = await fetch(`${API}/api/resource-library/upload/init`, { method: 'POST', headers, body: init });
  if (!initRes.ok) throw new Error((await initRes.json().catch(() => ({}))).detail || 'Could not start upload');
  const { file_id } = await initRes.json();

  const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  for (let i = 0; i < total; i += 1) {
    const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    const fd = new FormData();
    fd.append('chunk_index', String(i));
    fd.append('chunk', blob, `${file.name}.part${i}`);
    let ok = false;
    for (let attempt = 0; attempt < 3 && !ok; attempt += 1) {
      const r = await fetch(`${API}/api/resource-library/upload/${file_id}/chunk`, { method: 'POST', headers, body: fd });
      ok = r.ok;
    }
    if (!ok) throw new Error(`Upload failed at part ${i + 1}/${total}`);
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }

  const done = new FormData();
  done.append('total_chunks', String(total));
  const doneRes = await fetch(`${API}/api/resource-library/upload/${file_id}/complete`, { method: 'POST', headers, body: done });
  if (!doneRes.ok) throw new Error((await doneRes.json().catch(() => ({}))).detail || 'Could not finish upload');
  return (await doneRes.json()).resource;
}

export const isNewResource = (res) => !!res?.created_at && Date.now() - new Date(res.created_at).getTime() < 14 * 24 * 3600 * 1000;
